import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { getSiteConfig, updateSiteConfig } from "../lib/api";
import { toast } from "sonner";
import { Envelope, CheckCircle, Trash, ArrowLeft, User, Bell, PaperPlaneTilt } from "@phosphor-icons/react";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { hour12: false }) : "—";

const SCOPE_TAG = {
  central:              { label: "Central Newsroom",  bg: "bg-indigo-100",  fg: "text-indigo-700" },
  colleges:             { label: "Colleges News",     bg: "bg-emerald-100", fg: "text-emerald-700" },
  sched_pg_exam:        { label: "PG · Exam Sched",   bg: "bg-rose-100",    fg: "text-rose-700" },
  sched_ug_counselling: { label: "UG · Counselling",  bg: "bg-sky-100",     fg: "text-sky-700" },
};

const SCOPE_ORDER = ["central", "colleges", "sched_pg_exam", "sched_ug_counselling"];
const SCOPE_SHORT = {
  central:              "Central",
  colleges:             "Colleges",
  sched_pg_exam:        "PG Exam",
  sched_ug_counselling: "UG Couns",
};

const tagFor = (s) => SCOPE_TAG[s] || { label: s, bg: "bg-zinc-100", fg: "text-zinc-700" };

/**
 * Pivot a flat list of (email × scope) subscription rows into one row per email.
 * Each row gets a `subs` map keyed by scope so the table can render tick columns.
 */
function groupByEmail(items) {
  const map = new Map();
  for (const s of items) {
    if (!s.email) continue;
    let row = map.get(s.email);
    if (!row) {
      row = { email: s.email, subs: {}, ids: [], state: null, firstSeen: null };
      map.set(s.email, row);
    }
    row.subs[s.scope] = s;
    row.ids.push(s._id);
    if (s.news_state && !row.state) row.state = s.news_state;
    if (s.created_at && (!row.firstSeen || s.created_at < row.firstSeen)) row.firstSeen = s.created_at;
  }
  return Array.from(map.values()).sort((a, b) => (b.firstSeen || "").localeCompare(a.firstSeen || ""));
}

export default function AdminSubscribers() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [scope, setScope]       = useState("");
  const [verified, setVerified] = useState("");    // "", "true", "false"
  const [cfg, setCfg]           = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const loadCfg = async () => {
    try {
      const r = await getSiteConfig();
      setCfg(r.data);
    } catch (e) { /* non-fatal */ }
  };

  const setEmailMode = async (mode) => {
    if (savingCfg || cfg?.email_notifications === mode) return;
    setSavingCfg(true);
    try {
      await updateSiteConfig({ email_notifications: mode });
      // PUT returns merged config without the computed fields — refetch for effective state
      await loadCfg();
      toast.success(`Email sending set to ${mode.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSavingCfg(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (scope)         params.set("scope", scope);
      if (verified)      params.set("verified", verified);
      const r = await api.get(`/admin/subscribers?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load subscribers");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, verified]);
  useEffect(() => { loadCfg(); /* eslint-disable-next-line */ }, []);

  const removeEmail = async (email, ids) => {
    if (!window.confirm(`Remove ALL ${ids.length} subscription(s) for ${email}?`)) return;
    try {
      await Promise.all(ids.map((id) => api.delete(`/admin/subscribers/${id}`)));
      toast.success(`Removed ${ids.length} row(s)`);
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const news  = data?.news_subscribers;
  const notif = data?.notify_subscribers;
  const leads = data?.leads;

  return (
    <div className="min-h-screen bg-white" data-testid="admin-subscribers-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-xs font-bold uppercase text-zinc-500 hover:text-[#6043EF] flex items-center gap-1"
            data-testid="back-to-admin">
            <ArrowLeft size={12}/> Admin
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]">Subscribers</h1>
          {news && <span className="text-xs font-mono font-bold text-zinc-500">{news.total} total</span>}
        </div>

        {/* Email sending master switch */}
        <EmailToggle cfg={cfg} saving={savingCfg} onSet={setEmailMode} />

        {/* Top summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="subscribers-summary">
          <SummaryCard
            icon={Envelope}
            label="News subscribers"
            value={news?.total ?? "—"}
            sub="Newsroom / colleges / schedules"
          />
          <SummaryCard
            icon={Bell}
            label="Live-board waitlist"
            value={notif?.total ?? 0}
            sub={"“Notify me” from live dashboard"}
          />
          <SummaryCard
            icon={User}
            label="Leads"
            value={leads?.total ?? 0}
            sub="From college enquiry forms"
            to="/admin/leads"
          />
          <SummaryCard
            icon={CheckCircle}
            label="Verified news subs"
            value={(news?.groups || []).reduce((a, g) => a + (g.verified || 0), 0)}
            sub="Double-opt-in completed"
          />
        </div>

        {/* Per-scope breakdown */}
        {news?.groups?.length ? (
          <div className="mb-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">By subscription type</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {news.groups.map((g) => {
                const t = tagFor(g.scope);
                const isActive = scope === g.scope;
                return (
                  <button
                    key={g.scope}
                    onClick={() => setScope(isActive ? "" : g.scope)}
                    data-testid={`scope-card-${g.scope}`}
                    className={`text-left p-3 border-2 rounded-lg transition-colors ${
                      isActive ? "border-[#6043EF] bg-violet-50" : "border-zinc-200 hover:border-zinc-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${t.bg} ${t.fg}`}>
                        {t.label}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-[#09090B] font-mono">{g.total}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {g.verified} verified · {g.active} active
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3 items-center">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mr-1">Filter:</span>
          <button
            onClick={() => setScope("")}
            data-testid="filter-scope-all"
            className={`h-7 px-3 text-[10px] font-bold uppercase tracking-wider rounded-full ${
              !scope ? "bg-[#6043EF] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
            }`}
          >All scopes</button>
          {[["true", "Verified"], ["false", "Pending"], ["", "Any"]].map(([v, l]) => (
            <button
              key={l}
              onClick={() => setVerified(v)}
              data-testid={`filter-verified-${v || "any"}`}
              className={`h-7 px-3 text-[10px] font-bold uppercase tracking-wider rounded-full ${
                verified === v ? "bg-[#09090B] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
            >{l}</button>
          ))}
          {loading && <span className="text-[11px] text-zinc-400 ml-2">Loading…</span>}
        </div>

        {/* News subscribers table — one row per email, columns per scope */}
        <div className="border-2 border-zinc-200 rounded-lg overflow-hidden mb-8 overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="news-subs-table">
            <thead className="bg-zinc-50 border-b-2 border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                {SCOPE_ORDER.map((s) => (
                  <th key={s} className="text-center px-2" title={tagFor(s).label}>
                    <span className={`inline-block px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full ${tagFor(s).bg} ${tagFor(s).fg}`}>
                      {SCOPE_SHORT[s] || s}
                    </span>
                  </th>
                ))}
                <th className="text-left" title="State selected for Colleges News subscription (only applies to the Colleges scope)">Colleges&nbsp;state</th>
                <th className="text-left">First seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groupByEmail(news?.items || []).map((row) => (
                <tr key={row.email} data-testid={`sub-row-${row.email}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-2 font-mono text-[12px]">{row.email}</td>
                  {SCOPE_ORDER.map((s) => {
                    const sub = row.subs[s];
                    return (
                      <td key={s} className="text-center px-2" data-testid={`cell-${row.email}-${s}`}>
                        {!sub
                          ? <span className="text-zinc-200">—</span>
                          : sub.verified && sub.active
                            ? <CheckCircle size={16} weight="fill" className="text-emerald-500 inline" />
                            : sub.verified && !sub.active
                              ? <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider" title="Verified but currently unsubscribed">Unsub</span>
                              : <span className="text-[9px] font-bold text-amber-500 uppercase tracking-wider" title="Verification email sent, awaiting click on the link">Pending&nbsp;verify</span>}
                      </td>
                    );
                  })}
                  <td className="text-[12px] text-zinc-600">{row.state || "—"}</td>
                  <td className="text-[11px] font-mono text-zinc-500">{fmt(row.firstSeen)}</td>
                  <td className="pr-3">
                    <button
                      onClick={() => removeEmail(row.email, row.ids)}
                      data-testid={`delete-${row.email}`}
                      className="text-zinc-300 hover:text-rose-600 transition-colors"
                      title={`Remove all ${row.ids.length} subscription(s) for this email`}
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {news && news.items.length === 0 && (
                <tr><td colSpan={SCOPE_ORDER.length + 4} className="p-6 text-center text-zinc-400 text-sm">No subscribers match this filter.</td></tr>
              )}
            </tbody>
            {news?.items?.length > 0 && (
              <tfoot className="bg-zinc-50 border-t-2 border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                <tr>
                  <td className="px-3 py-2">{groupByEmail(news.items).length} unique emails</td>
                  {SCOPE_ORDER.map((s) => (
                    <td key={s} className="text-center px-2 font-mono">
                      {news.items.filter((x) => x.scope === s && x.verified && x.active).length}
                    </td>
                  ))}
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Notify-me waitlist */}
        {notif && notif.items.length > 0 && (
          <div className="mb-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Live-board “Notify me” waitlist</p>
            <div className="border-2 border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="notify-subs-table">
                <thead className="bg-zinc-50 border-b-2 border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="text-left">Source</th>
                    <th className="text-left">Since</th>
                  </tr>
                </thead>
                <tbody>
                  {notif.items.map((n, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2 font-mono text-[12px]">{n.email}</td>
                      <td className="text-[12px] text-zinc-600">{n.source || "live-board"}</td>
                      <td className="text-[11px] font-mono text-zinc-500">{fmt(n.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EmailToggle({ cfg, saving, onSet }) {
  const mode = cfg?.email_notifications || "auto";
  const effective = cfg?.email_notifications_effective;
  const isPreview = cfg?.email_notifications_is_preview;
  const MODES = [
    ["on",   "On"],
    ["off",  "Off"],
    ["auto", "Auto"],
  ];
  const on = effective === true;
  return (
    <div className="mb-6 border-2 border-zinc-200 rounded-lg p-4" data-testid="email-toggle-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-2">
          <PaperPlaneTilt size={18} weight="bold" className="text-[#6043EF] mt-0.5" />
          <div>
            <p className="text-sm font-bold text-[#09090B]">Send subscriber emails</p>
            <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed max-w-lg">
              Master switch for ALL outgoing emails (news alerts, schedule alerts, verification &amp; welcome digests).
              <b> Auto</b> = OFF on preview, ON in production. Crawlers keep running either way; only the emails are held.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {cfg && (
            <span
              data-testid="email-toggle-effective"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                on ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-500" : "bg-rose-500"}`} />
              {on ? "Sending" : "Paused"}
            </span>
          )}
          <div className="flex rounded-full bg-zinc-100 p-0.5" role="group">
            {MODES.map(([v, label]) => (
              <button
                key={v}
                disabled={saving || !cfg}
                onClick={() => onSet(v)}
                data-testid={`email-mode-${v}`}
                className={`h-7 px-3 text-[11px] font-bold uppercase tracking-wider rounded-full transition-colors disabled:opacity-50 ${
                  mode === v ? "bg-[#09090B] text-white" : "text-zinc-600 hover:text-zinc-900"
                }`}
              >{label}</button>
            ))}
          </div>
        </div>
      </div>
      {cfg && mode === "auto" && (
        <p className="text-[10px] text-zinc-400 mt-2" data-testid="email-toggle-auto-note">
          Auto resolved to <b>{on ? "ON" : "OFF"}</b> — this pod is {isPreview ? "a preview environment" : "production"}.
        </p>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, to }) {
  const inner = (
    <>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} weight="bold" className="text-[#6043EF]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#52525B]">{label}</span>
      </div>
      <div className="text-3xl font-bold font-mono text-[#09090B]">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>
    </>
  );
  const cls = "block p-4 border-2 border-zinc-200 rounded-lg bg-white hover:border-[#6043EF] transition-colors";
  return to
    ? <Link to={to} className={cls} data-testid={`sum-${label.toLowerCase().replace(/\s/g,"-")}`}>{inner}</Link>
    : <div className={cls} data-testid={`sum-${label.toLowerCase().replace(/\s/g,"-")}`}>{inner}</div>;
}
